import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const API_URL = process.env.REACT_APP_API_URL || '/api/v1';

export const DEFAULT_PROFILE_ID =
  process.env.REACT_APP_PROFILE_ID || '00000000-0000-0000-0000-000000000001';

// ─── Device ID ────────────────────────────────────────────
const getDeviceId = () => {
  let deviceId = localStorage.getItem('kids_universe_device_id');
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem('kids_universe_device_id', deviceId);
  }
  return deviceId;
};

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// اضافه کردن x-device-id به همه درخواست‌ها
api.interceptors.request.use((config) => {
  config.headers['x-device-id'] = getDeviceId();
  return config;
});

api.interceptors.response.use(
  (res) => res.data,
  (err) => {
    const msg = err.response?.data?.error || err.message || 'Something went wrong';
    console.error('API Error:', msg);
    return Promise.reject(new Error(msg));
  }
);

// ─── Profile ──────────────────────────────────────────────
export const profileAPI = {
  getOrCreate: (name) => api.get('/profile', { params: { name } }),
  getById:     (id)   => api.get(`/profile/${id}`),
  update:      (id, data) => api.patch(`/profile/${id}`, data),
};

// ─── Games ────────────────────────────────────────────────
export const gamesAPI = {
  getAll:         ()          => api.get('/games').then(r => Array.isArray(r) ? r : (r.games || [])),
  getProgress:    (profileId) => api.get(`/games/progress/${profileId}`).then(r => Array.isArray(r) ? r : (r.progress || [])),
  saveResult:     (data)      => api.post('/games/result', data),
  getLeaderboard: (gameId)    => api.get(`/games/${gameId}/leaderboard`).then(r => r.leaderboard || []),
};

// ─── Stories ──────────────────────────────────────────────
export const storiesAPI = {
  getTemplates: ()                     => api.get('/stories/templates').then(r => r.templates || []),
  getAll:       (profileId)            => api.get(`/stories/${profileId}`).then(r => Array.isArray(r) ? r : (r.stories || [])),
  getOne:       (profileId, storyId)   => api.get(`/stories/${profileId}/${storyId}`),
  create:       (data)                 => api.post('/stories', data),
  update:       (profileId, id, data)  => api.put(`/stories/${profileId}/${id}`, data),
  delete:       (profileId, id)        => api.delete(`/stories/${profileId}/${id}`),
};

// ─── Drawings ─────────────────────────────────────────────
export const drawingsAPI = {
  getAll:  (profileId)            => api.get(`/drawings/${profileId}`).then(r => Array.isArray(r) ? r : (r.drawings || [])),
  getOne:  (profileId, drawingId) => api.get(`/drawings/${profileId}/${drawingId}`),
  save:    (data)                 => api.post('/drawings', data),
  delete:  (profileId, id)        => api.delete(`/drawings/${profileId}/${id}`),
};

// ─── Diary ────────────────────────────────────────────────
export const diaryAPI = {
  getAll:  (profileId)           => api.get(`/diary/${profileId}`).then(r => Array.isArray(r) ? r : (r.entries || [])),
  getOne:  (profileId, entryId)  => api.get(`/diary/${profileId}/${entryId}`),
  create:  (data)                => api.post('/diary', data),
  update:  (profileId, id, data) => api.put(`/diary/${profileId}/${id}`, data),
};

// ─── Learning ─────────────────────────────────────────────
export const learningAPI = {
  getModules:   ()          => api.get('/learning').then(r => Array.isArray(r) ? r : (r.modules || [])),
  getModule:    (moduleId)  => api.get(`/learning/${moduleId}`),
  getProgress:  (profileId) => api.get(`/learning/progress/${profileId}`).then(r => Array.isArray(r) ? r : (r.progress || [])),
  saveProgress: (data)      => api.post('/learning/progress', data),
};

// ─── Points ───────────────────────────────────────────────
export const pointsAPI = {
  getLog: (profileId) => api.get(`/points/${profileId}`).then(r => Array.isArray(r) ? r : (r.log || r.points || [])),
  add:    (data)      => api.post('/points', data),
};

// ─── Achievements ─────────────────────────────────────────
export const achievementsAPI = {
  getAll: (profileId) => api.get(`/achievements/${profileId}`).then(r => Array.isArray(r) ? r : (r.achievements || [])),
};

export default api;